/**
 * AlivenessStrip — the fleet's voice (consult idea #2, tier S).
 *
 * One thin strip under the Fleet HUD header showing the latest REAL journal
 * line for the active run: level-colored tag, real timestamp, journal age
 * recomputed every second via useNow, and a live poll countdown. Click (a
 * user gesture — lawful motion source) expands an 8-line orange-phosphor
 * terminal of the real journal tail.
 *
 * Motion contract (docs/plans/2026-08-12-hud-cinema-consult.md):
 * - New lines slide up, transform-only; opacity fade on EXIT only.
 * - No animation sourced from elapsed time — ticks are now − realTimestamp.
 * - prefers-reduced-motion collapses every variant to instant.
 * - Idle (no active run) renders one quiet static line. No motion.
 */
import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FleetJournalEntry } from '../../services/fleetHudApi';
import { FleetMood, MOOD_STYLE, useNow } from './fleetMood';
import { ACCENT } from './ReactorCore';
import { formatElapsedSeconds, parseTs } from './fleetTime';

interface AlivenessStripProps {
  activeRunId: string | null;
  lastJournalLine: FleetJournalEntry | null;
  journalAgeSeconds: number | null;
  /** Poll interval in seconds — the countdown resets to this each poll. */
  pollCountdownSeconds: number;
  mood: FleetMood;
  /** Real journal tail (newest last) — rendered by the expanded terminal. */
  journalTail: FleetJournalEntry[];
  /** Epoch ms of the last successful poll — anchors the live countdown. */
  polledAt: number | null;
  /** 'strip' (default, wave-1 inline card) or 'bar' — the bridge's fixed
      36px bottom log bar: expands UPWARD as a 220px overlay (no grid
      reflow) with a persisted pin-open state. */
  variant?: 'strip' | 'bar';
}

/**
 * Entry motion for a newly arrived journal line. Transform-only slide-up;
 * reduced motion collapses initial === animate with zero duration. Exported
 * so the reduced-motion path is unit-testable (it must never be dead code).
 */
export function entryMotion(reduced: boolean): {
  initial: { y: number };
  animate: { y: number };
  transition: { duration: number; ease?: 'easeOut' };
} {
  return reduced
    ? { initial: { y: 0 }, animate: { y: 0 }, transition: { duration: 0 } }
    : { initial: { y: 8 }, animate: { y: 0 }, transition: { duration: 0.3, ease: 'easeOut' } };
}

/** Expand/collapse container motion — user-gesture sourced, height animate. */
export function expandMotion(reduced: boolean): {
  initial: { height: number | 'auto'; opacity: number };
  animate: { height: number | 'auto'; opacity: number };
  exit: { height: number | 'auto'; opacity: number };
  transition: { duration: number };
} {
  return reduced
    ? {
        initial: { height: 'auto', opacity: 1 },
        animate: { height: 'auto', opacity: 1 },
        exit: { height: 'auto', opacity: 1 },
        transition: { duration: 0 },
      }
    : {
        initial: { height: 0, opacity: 0 },
        animate: { height: 'auto', opacity: 1 },
        exit: { height: 0, opacity: 0 },
        transition: { duration: 0.25 },
      };
}

function clockTime(ts?: string): string {
  const ms = parseTs(ts);
  if (ms === null) return '--:--:--';
  return new Date(ms).toLocaleTimeString('en-GB', { hour12: false });
}

/** Level tag — hierarchy within the near-black + #FF6600 palette only. */ // design-law-allow
function LevelTag({ level }: { level?: string }) {
  const lv = (level ?? 'info').toLowerCase();
  const cls =
    lv === 'error'
      ? `bg-[${ACCENT}] text-black`
      : lv === 'warning' || lv === 'warn'
        ? `border border-[${ACCENT}] text-[${ACCENT}]`
        : 'border border-hairline text-text-muted';
  return (
    <span className={`inline-block shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${cls}`}>
      {lv}
    </span>
  );
}

/** localStorage key for the bridge log bar's pin-open state (bar variant). */
export const LOGBAR_PIN_KEY = 'fleet-logbar-pinned';

function readPinned(): boolean {
  try {
    return window.localStorage.getItem(LOGBAR_PIN_KEY) === '1';
  } catch {
    return false;
  }
}

export default function AlivenessStrip({
  activeRunId,
  lastJournalLine,
  journalAgeSeconds,
  pollCountdownSeconds,
  mood,
  journalTail,
  polledAt,
  variant = 'strip',
}: AlivenessStripProps) {
  const reduced = useReducedMotion() ?? false;
  const bar = variant === 'bar';
  // Bar variant: pin-open persists across sessions (the mission transcript
  // stays up if you pinned it). Strip variant keeps the wave-1 behavior.
  const [pinned, setPinned] = useState<boolean>(() => (bar ? readPinned() : false));
  const [expanded, setExpanded] = useState<boolean>(() => (bar ? readPinned() : false));
  const now = useNow(1000);

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    if (next) setExpanded(true);
    try {
      window.localStorage.setItem(LOGBAR_PIN_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable — pin is session-only */
    }
  };

  const rootClass = bar
    ? 'relative h-9 w-full border-t border-hairline bg-surface'
    : 'rounded-lg border border-hairline bg-surface';

  // Idle — one quiet static line. No motion; the bar keeps its frame.
  if (activeRunId === null) {
    return (
      <div className={bar ? rootClass : 'rounded-lg border border-hairline bg-surface px-4 py-2'}>
        <p className={`font-mono text-xs text-text-muted ${bar ? 'flex h-full items-center px-4' : ''}`}>
          fleet idle — last journal {formatElapsedSeconds(journalAgeSeconds)} ago
        </p>
      </div>
    );
  }

  // Journal age: prefer the line's REAL timestamp (ticks every second via
  // useNow); fall back to the overview's poll-time age when no line is loaded.
  const lineMs = lastJournalLine ? parseTs(lastJournalLine.ts) : null;
  const journalAge =
    lineMs !== null ? Math.max(0, (now - lineMs) / 1000) : journalAgeSeconds;

  // Poll countdown: anchored to the last successful poll, ticked by useNow.
  const countdown =
    polledAt !== null
      ? Math.max(0, Math.ceil(pollCountdownSeconds - (now - polledAt) / 1000))
      : null;

  const lineKey = lastJournalLine
    ? `${lastJournalLine.ts ?? ''}|${lastJournalLine.message ?? ''}`
    : 'empty';
  const entry = entryMotion(reduced);
  const expand = expandMotion(reduced);

  return (
    <div className={rootClass}>
      <button
        type="button"
        onClick={() => {
          // A pinned bar stays open — unpin to collapse.
          if (bar && pinned) return;
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
        className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 text-left ${
          bar ? 'h-9 flex-nowrap overflow-hidden' : 'py-2'
        }`}
      >
        {/* Mood dot — glow bound to the derived mood, never to a timer */}
        <span
          className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[${ACCENT}]`}
          style={{ boxShadow: `0 0 8px ${MOOD_STYLE[mood].glow}` }}
          title={MOOD_STYLE[mood].label}
        />
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={lineKey}
            initial={entry.initial}
            animate={entry.animate}
            exit={{ opacity: 0 }}
            transition={entry.transition}
            className="min-w-0 flex-1 truncate font-mono text-xs text-text-primary"
          >
            {lastJournalLine ? (
              <>
                <LevelTag level={lastJournalLine.level} />{' '}
                <span className="text-text-muted">{clockTime(lastJournalLine.ts)}</span>{' '}
                {lastJournalLine.message ?? ''}
              </>
            ) : (
              <span className="text-text-muted">awaiting journal…</span>
            )}
          </motion.span>
        </AnimatePresence>
        <span className="shrink-0 font-mono text-[11px] text-text-muted">
          journal {formatElapsedSeconds(journalAge)} ago
        </span>
        <span className="shrink-0 font-mono text-[11px] text-text-muted">
          next refresh in {countdown !== null ? `${countdown}s` : '—'}
        </span>
      </button>
      {bar && (
        <button
          type="button"
          onClick={togglePin}
          aria-pressed={pinned}
          title={pinned ? 'Unpin transcript' : 'Pin transcript open'}
          className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] ${
            pinned ? `border-[${ACCENT}] text-[${ACCENT}]` : 'border-white/[0.06] text-text-muted'
          }`}
          data-testid="logbar-pin"
        >
          pin
        </button>
      )}

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="terminal"
            initial={expand.initial}
            animate={expand.animate}
            exit={expand.exit}
            transition={expand.transition}
            className={
              // Bar variant expands UPWARD as an absolute overlay — the grid
              // never reflows. Scrollback stays capped at the fetched 8-line
              // tail (no new endpoint; quote-only rendering below).
              bar
                ? 'absolute bottom-full left-0 right-0 h-[220px] overflow-y-auto border border-hairline bg-base shadow-[0_-8px_24px_rgba(0,0,0,0.5)]'
                : 'overflow-hidden'
            }
          >
            {/* 8-line orange-phosphor terminal — real journal, real timestamps */}
            <div className={`border-t border-hairline bg-base px-4 py-2 font-mono text-xs text-[${ACCENT}]`}>
              <p className="mb-1 text-[10px] uppercase tracking-wide text-text-muted">
                journal tail · {activeRunId} · as of {clockTime(lastJournalLine?.ts)}
              </p>
              {journalTail.length === 0 ? (
                <p className="text-text-muted">no journal lines yet</p>
              ) : (
                journalTail.slice(-8).map((l, i) => (
                  <p key={`${l.ts ?? i}|${i}`} className="truncate leading-5">
                    <span className="text-text-muted">{clockTime(l.ts)}</span>{' '}
                    <span className="uppercase">[{(l.level ?? 'info').toLowerCase()}]</span>{' '}
                    {l.message ?? ''}
                  </p>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
